import { RouteProp, useRoute } from '@react-navigation/native';
import LongTermParkingContext from 'context/LongTermParkingContext';
import { useContext, useEffect, useMemo } from 'react';
import { useCityParkingStore } from 'stores/cityParkingStore';
import { usePermitStore } from 'stores/permitStore';
import { Pmc } from 'types/generatedSchemaTypes';
import { CityParkingParamList, LongTermStackParamList } from 'types/navigation';
import { getParkingZoneName, getPriceForSalesOrder } from 'utils/commonUtils';
import { initSalesOrderPmcPurchase } from 'utils/permitUtils';

const getSelectedPmc = (
  selectedPmcs: Pmc[],
  routeName: string,
  pmcId: string
) => {
  if (routeName === 'purchase') {
    return useCityParkingStore.getState().selectedPmc ?? undefined;
  }
  return selectedPmcs?.find((pmc) => pmc.pmcId === pmcId);
};

const usePermitPurchaseInfo = () => {
  const { selectedPmcs = [] } = useContext(LongTermParkingContext);

  const { params, name: routeName } =
    useRoute<
      RouteProp<
        LongTermStackParamList & CityParkingParamList,
        'buyScreen' | 'purchase' | 'waitingListJoin'
      >
    >();
  const pmcId = params?.pmcId ?? '';

  const selectedPmc = useMemo(() => {
    return getSelectedPmc(selectedPmcs, routeName, pmcId);
  }, [selectedPmcs, routeName, pmcId]);

  const startDate =
    usePermitStore((state) => state.selectedPermitStartDate) ?? new Date();
  const { selectedBillingFrequency } =
    usePermitStore((state) => state.selectedPermitBillingInterval) ?? {};

  const {
    prices,
    parkZone,
    salesLocation,
    countryCode = 'FI',
  } = selectedPmc ?? {};

  const placeName = parkZone
    ? getParkingZoneName(parkZone)
    : salesLocation?.name || '';

  const price = getPriceForSalesOrder(
    prices,
    countryCode,
    startDate,
    selectedBillingFrequency
  );

  useEffect(() => initSalesOrderPmcPurchase(selectedPmc), [selectedPmc]);

  return {
    selectedPmc,
    price,
    placeName,
    startDate,
    selectedBillingFrequency,
    params,
  };
};

export default usePermitPurchaseInfo;
